import { TopBar, Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MapPin, Mail, Instagram, Facebook, Send, Loader2, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useState, useEffect } from 'react';
import { trackEvent } from '@/lib/analytics';
import { motion } from 'framer-motion';
import { PageMeta, BreadcrumbJsonLd } from '@/components/PageMeta';
import { PageHero } from '@/components/PageHero';
import { ScrollProgressBar } from '@/components/ScrollProgressBar';
import { BOX_IMAGES } from '@/data/images';
import axios from 'axios';
import { API } from '@/lib/api';

const EASE = [0.22, 1, 0.36, 1];


export default function ContactPage() {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message,
      });
      toast.success(data?.message || 'Message envoyé ! Nous vous recontactons rapidement.', { duration: 6000 });
      trackEvent('contact_form_submit', { form: 'contact_page' });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(' • ')
          : "Une erreur est survenue. Merci de réessayer ou de nous écrire directement à crossfit.saintchamond@gmail.com";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const [info, setInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/content/contact-info`).then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const contactInfo = info
    ? [
        { icon: MapPin, title: 'Adresse', content: info.address },
        { icon: Mail, title: 'Email', content: info.email, href: `mailto:${info.email}` },
        info.instagram && { icon: Instagram, title: 'Instagram', content: '@crossfit_saintchamond', href: info.instagram },
        info.facebook && { icon: Facebook, title: 'Facebook', content: 'CrossFit Saint-Chamond', href: info.facebook },
      ].filter(Boolean)
    : [
        { icon: MapPin, title: 'Adresse', content: '7 Rue Jean-Jacques Rousseau, 42400 Saint-Chamond' },
        { icon: Mail, title: 'Email', content: 'crossfit.saintchamond@gmail.com', href: 'mailto:crossfit.saintchamond@gmail.com' },
        { icon: Instagram, title: 'Instagram', content: '@crossfit_saintchamond', href: 'https://www.instagram.com/crossfit_saintchamond' },
      ];

  return (
    <div className="min-h-screen">
      <PageMeta title="Contact" description="Contactez CrossFit Saint-Chamond. 7 Rue Jean-Jacques Rousseau, 42400 Saint-Chamond. Email: crossfit.saintchamond@gmail.com. Instagram: @crossfit_saintchamond." path="/contact" />
      <BreadcrumbJsonLd items={[{ name: 'Accueil', url: '/' }, { name: 'Contact' }]} />
      <ScrollProgressBar />
      <TopBar />
      <Navigation />

      <PageHero
        pageKey="/contact"
        eyebrow="Contact"
        title="CONTACTEZ-NOUS"
        highlight="NOUS"
        subtitle="Une question ? Envie d'essayer ? Écrivez-nous ou passe directement à la box !"
        image={BOX_IMAGES.barbellPrep}
        alt="CrossFit Saint-Chamond - Contactez-nous"
        breadcrumbs={[{ name: 'Accueil', href: '/' }, { name: 'Contact' }]}
        minHeight="min-h-[55vh]"
      />

      {/* Form + Info */}
      <section className="section-padding bg-white" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-10">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10%' }}
              transition={{ duration: 0.8, ease: EASE }}
              className="lg:col-span-3"
            >
              <Card className="p-6 md:p-8 border-border rounded-2xl shadow-sm" data-testid="contact-form-card">
                <h2 className="font-heading text-2xl md:text-3xl text-dark mb-6 tracking-wide">
                  ENVOYEZ UN <span className="text-gradient-primary italic font-display inline-block pr-2">MESSAGE</span>
                </h2>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="text-sm font-medium">Nom complet *</Label>
                      <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Votre nom" required className="mt-1.5" data-testid="contact-name-input" />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm font-medium">Email *</Label>
                      <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="votre@email.com" required className="mt-1.5" data-testid="contact-email-input" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium">Téléphone</Label>
                    <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="+33 6 12 34 56 78" className="mt-1.5" data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-sm font-medium">Message *</Label>
                    <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="Parlez-nous de vos objectifs..." required rows={5} className="mt-1.5" data-testid="contact-message-input" />
                  </div>
                  <Button type="submit" disabled={submitting} className="btn-shine w-full bg-primary hover:bg-primary-light text-white font-heading tracking-wider text-sm py-7 rounded-xl glow-primary group disabled:opacity-60 disabled:cursor-not-allowed" size="lg" data-testid="contact-submit-button">
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ENVOI EN COURS...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                        ENVOYER LE MESSAGE
                      </>
                    )}
                  </Button>
                </form>
              </Card>
            </motion.div>

            {/* Info Cards */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10%' }}
              transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
              className="lg:col-span-2 space-y-4"
            >
              {contactInfo.map((info, idx) => {
                const Icon = info.icon;
                return (
                  <Card key={idx} className="p-5 border-border rounded-xl hover:border-primary/40 hover:shadow-md transition-all duration-300" data-testid={`contact-info-${info.title.toLowerCase()}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="h-5 w-5 text-primary" strokeWidth={2.2} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm text-dark mb-0.5 tracking-wide">{info.title}</h3>
                        {info.href ? (
                          <a href={info.href} target={info.href.startsWith('http') ? '_blank' : undefined} rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined} className="text-sm text-primary hover:underline">
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-sm text-muted-foreground">{info.content}</p>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}

              {info?.google_business_url && (
                <a
                  href={info.google_business_url}
                  target="_blank"
                  rel="noreferrer"
                  className="block group"
                  data-testid="google-review-cta"
                >
                  <Card className="p-5 border-primary/30 bg-gradient-to-br from-primary/5 to-white rounded-xl hover:shadow-lg transition-all">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Star className="h-5 w-5 text-primary fill-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-sm text-dark tracking-wide">LAISSE UN AVIS GOOGLE</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Ton retour aide d'autres sportifs à nous découvrir ⭐
                        </p>
                      </div>
                    </div>
                  </Card>
                </a>
              )}

              <Card className="overflow-hidden h-[280px] rounded-xl border-border" data-testid="contact-map">
                <iframe
                  src={`https://www.google.com/maps?q=${encodeURIComponent(info?.maps_embed_query || '7 Rue Jean-Jacques Rousseau, 42400 Saint-Chamond, France')}&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="CrossFit Saint-Chamond"
                />
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
